import { useMemo, useRef, useState } from "preact/hooks";
import { useTranslation } from "react-i18next";
import { Typography } from "../../../shared/ui";
import {
  TEXT_MAX_CODE_POINTS,
  TEXT_MAX_UTF8_BYTES,
  TextFault,
  TextFaultCode,
  validateText,
} from "../model/text";
import css from "./ChatComposer.module.scss";

interface ChatComposerProps {
  disabled?: boolean;
  onSend: (text: string) => void;
}

/** Подсказки под полем ввода. */
const FAULT_HINT_KEY: Record<TextFaultCode, string> = {
  [TextFault.MalformedSurrogate]: "chat.hint_invalid_char",
  [TextFault.TooLong]: "chat.hint_too_long",
  [TextFault.TooLarge]: "chat.hint_too_large",
};

export function ChatComposer({ disabled, onSend }: ChatComposerProps) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState("");
  const areaRef = useRef<HTMLTextAreaElement>(null);

  const validation = useMemo(() => validateText(draft), [draft]);
  const canSend = !disabled && validation.canSend;

  // Оба лимита действуют одновременно после канонизации (§6.1).
  const nearCodePointLimit = validation.codePoints > TEXT_MAX_CODE_POINTS - 40;
  const nearByteLimit = validation.utf8Bytes > TEXT_MAX_UTF8_BYTES - 120;
  const overLimit =
    validation.codePoints > TEXT_MAX_CODE_POINTS ||
    validation.utf8Bytes > TEXT_MAX_UTF8_BYTES;

  const submit = () => {
    if (!canSend) return;
    onSend(validation.canonical);
    setDraft("");
    areaRef.current?.focus();
  };

  const onKeyDown = (event: KeyboardEvent) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      submit();
    }
  };

  const hint = validation.fault
    ? t(FAULT_HINT_KEY[validation.fault], { limit: TEXT_MAX_CODE_POINTS })
    : null;

  return (
    <div className={css.wrapper}>
      {hint && (
        <div className={css.hint}>
          <Typography variant="caption3" color="orange">
            {hint}
          </Typography>
        </div>
      )}

      <div className={`${css.inputRow} ${overLimit ? css.invalid : ""}`}>
        <textarea
          ref={areaRef}
          className={css.input}
          value={draft}
          rows={1}
          disabled={disabled}
          placeholder={disabled ? t("chat.unavailable") : t("chat.placeholder")}
          onInput={(e) => setDraft((e.target as HTMLTextAreaElement).value)}
          onKeyDown={onKeyDown}
        />

        <button
          type="button"
          className={css.send}
          disabled={!canSend}
          onClick={submit}
          aria-label={t("chat.send")}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path
              d="M3.5 5.2L20.5 12L3.5 18.8L6.5 12L3.5 5.2Z"
              stroke="currentColor"
              stroke-width="1.4"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              d="M6.5 12H13"
              stroke="currentColor"
              stroke-width="1.4"
              stroke-linecap="round"
            />
          </svg>
        </button>
      </div>

      {(nearCodePointLimit || nearByteLimit) && (
        <div className={css.counters}>
          <Typography
            variant="caption3"
            color={
              validation.codePoints > TEXT_MAX_CODE_POINTS
                ? "system-red"
                : "grey"
            }
          >
            {validation.codePoints}/{TEXT_MAX_CODE_POINTS}
          </Typography>
          {nearByteLimit && (
            <Typography
              variant="caption3"
              color={
                validation.utf8Bytes > TEXT_MAX_UTF8_BYTES
                  ? "system-red"
                  : "grey"
              }
            >
              {validation.utf8Bytes}/{TEXT_MAX_UTF8_BYTES} B
            </Typography>
          )}
        </div>
      )}
    </div>
  );
}
